/* eslint-disable */
import { useEffect, useRef, useState, useCallback } from "react";
import { io, Socket } from "socket.io-client";

type useChatSocketArgs = { userId?: string; token?: string };

function now() {
  return new Date().toISOString();
}

export default function useChatSocket({ userId, token }: useChatSocketArgs) {
  const localUserId =
    typeof window !== "undefined"
      ? localStorage.getItem("userId") ?? userId ?? ""
      : userId ?? "";
  const [socket, setSocket] = useState<Socket | null>(null);
  const onlineRef = useRef<Set<string>>(new Set());
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const typingTimers = useRef<Record<string, number>>({});
  const lastTypingEmit = useRef<number>(0);
  const joinedRooms = useRef<Set<string>>(new Set());

  const [unreadMap, setUnreadMap] = useState<Record<string, number>>({});
  const [lastMessage, setLastMessage] = useState<any>(null);

  // NEW: leave count state (for leave:created events)
  const [leaveCount, setLeaveCount] = useState<number>(0);

  const syncOnline = useCallback(
    () => setOnlineUsers(Array.from(onlineRef.current)),
    []
  );

  function extractConvAndSender(payload: any) {
    const conversationId =
      payload?.conversationId ??
      payload?.room ??
      payload?.conversation ??
      payload?.conversation_id ??
      (payload?.message &&
        (payload.message.conversationId ?? payload.message.room)) ??
      undefined;
    const sender =
      payload?.sender ??
      payload?.from ??
      payload?.userId ??
      payload?.user_id ??
      payload?.message?.sender;
    return {
      conversationId: conversationId ? String(conversationId) : undefined,
      sender,
    };
  }

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const raw = sessionStorage.getItem("chat_presence_v1");
      if (raw) {
        const arr = JSON.parse(raw) as string[];
        if (Array.isArray(arr)) {
          onlineRef.current = new Set(arr.map((s) => String(s)));
          setOnlineUsers(Array.from(onlineRef.current));
          console.debug(
            now(),
            "[chatSocket] restored presence from sessionStorage",
            arr
          );
        }
      }
    } catch (e) {
      console.warn("[chatSocket] restore presence failed", e);
    }
  }, []);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      sessionStorage.setItem(
        "chat_presence_v1",
        JSON.stringify(Array.from(onlineRef.current))
      );
    } catch (e) {
      console.warn("[chatSocket] persist presence failed", e);
    }
  }, [onlineUsers]);

  useEffect(() => {
    const url = process.env.REACT_APP_SOCKET_URL as string;
    if (!url) {
      console.warn(
        "[chatSocket] REACT_APP_SOCKET_URL not set — socket disabled"
      );
      return;
    }

    const OFFLINE_GRACE_MS = 3000; // 3 seconds
    const pendingOfflineTimers: Record<string, number> = {};

    const instance = io(url, {
      transports: ["websocket", "polling"],
      auth: token ? { token, userId: localUserId } : { userId: localUserId },
      query: localUserId ? { userId: localUserId } : undefined,
      withCredentials: true,
      autoConnect: true,
    });

    setSocket(instance);

    instance.on("connect", () => {
      console.debug(now(), "[chatSocket] connected", {
        id: instance.id,
        userId: localUserId,
      });

      if (localUserId) {
        try {
          onlineRef.current.add(String(localUserId));
          syncOnline();
          console.debug(
            now(),
            "[chatSocket] optimistic local online add",
            localUserId
          );
        } catch (e) {
          console.warn("[chatSocket] optimistic local online add failed", e);
        }
      }

      if (localUserId) {
        try {
          instance.emit("identify", { userId: localUserId });
          console.debug(now(), "[chatSocket] emit identify", {
            userId: localUserId,
          });
        } catch (e) {
          console.warn("[chatSocket] identify emit failed", e);
        }
      } else {
        console.debug(now(), "[chatSocket] no userId available to identify");
      }

      if (token) {
        try {
          instance.emit("authenticate", { token });
        } catch (e) {
          console.warn("[chatSocket] authenticate emit failed", e);
        }
      }

      try {
        instance.emit("get_unread");
      } catch {}

      // OPTIONAL: if your backend supports asking for unread leave count on connect
      // you can emit something like 'get_leave_unread' here. Not changing that behavior by default.
    });

    instance.on("user:online", (d: any) => {
      // console.debug(now(), "[chatSocket] user:online", d);
      try {
        const id = typeof d === "string" ? d : d?.userId ?? d?.id;
        if (!id) return;
        const key = String(id);
        if (pendingOfflineTimers[key]) {
          window.clearTimeout(pendingOfflineTimers[key]);
          delete pendingOfflineTimers[key];
          console.debug(
            now(),
            "[chatSocket] cancelled pending offline timer for",
            key
          );
        }
        onlineRef.current.add(String(key));
        syncOnline();
      } catch (e) {
        console.warn("[chatSocket] user:online handler failed", e);
      }
    });

    instance.on("user:offline", (d: any) => {
      // console.debug(now(), "[chatSocket] user:offline", d);
      try {
        const id = typeof d === "string" ? d : d?.userId ?? d?.id;
        if (!id) return;
        const key = String(id);

        if (key === String(localUserId) && instance && instance.connected) {
          console.debug(
            now(),
            "[chatSocket] ignoring user:offline for localUserId while socket connected"
          );
          return;
        }

        if (pendingOfflineTimers[key]) {
          window.clearTimeout(pendingOfflineTimers[key]);
        }

        pendingOfflineTimers[key] = window.setTimeout(() => {
          try {
            console.debug(
              now(),
              "[chatSocket] applying offline after grace for",
              key
            );
            onlineRef.current.delete(String(key));
            syncOnline();
          } catch (inner) {
            console.warn("[chatSocket] soft-offline removal failed", inner);
          } finally {
            delete pendingOfflineTimers[key];
          }
        }, OFFLINE_GRACE_MS) as unknown as number;
      } catch (e) {
        console.warn("[chatSocket] user:offline handler failed", e);
      }
    });

    instance.on("chat:unread", (payload: any) => {
      console.log(now(), "[chatSocket] chat:unread", payload);
      try {
        console.log(now(), " chat:unread payload", payload);
        if (!payload) return;
        if (Array.isArray(payload)) {
          const map: Record<string, number> = {};
          payload.forEach((p) => {
            const id = String(
              p.conversationId ?? p.id ?? p.room ?? p.convo ?? ""
            );
            if (id) map[id] = Number(p.unread ?? p.count ?? 0);
          });
          setUnreadMap(map);
        } else if (typeof payload === "object") {
          const isMap = Object.values(payload).every(
            (v) => typeof v === "number"
          );
          if (isMap) {
            setUnreadMap((prev) => ({ ...prev, ...payload }));
          } else {
            const id = String(
              payload.conversationId ??
                payload.id ??
                payload.room ??
                payload.convo ??
                ""
            );
            if (id) {
              setUnreadMap((prev) => ({
                ...prev,
                [id]: Number(payload.unread ?? payload.count ?? 0),
              }));
            }
          }
        }
      } catch (e) {
        console.warn(now(), "chat:unread parse failed", e);
      }
    });

    const handleIncomingMessage = (payload: any) => {
      console.log(now(), "[chatSocket] incoming message", payload);

      try {
        setLastMessage(payload);
        const { conversationId, sender } = extractConvAndSender(payload);
        const convId =
          conversationId ??
          payload?.conversationId ??
          payload?.room ??
          payload?.conversation;
        const fromId = sender ?? payload?.sender ?? payload?.from;
        if (!convId) return;
        if (fromId && String(fromId) === String(localUserId)) {
          return;
        }
        if (joinedRooms.current.has(String(convId))) {
          return;
        }

        setUnreadMap((prev) => {
          const cur = Number(prev[String(convId)] ?? 0);
          return { ...prev, [String(convId)]: cur + 1 };
        });
      } catch (e) {
        console.warn(now(), "handleIncomingMessage failed", e);
      }
    };

    instance.on("chat:message", handleIncomingMessage);
    instance.on("chat:new_message", handleIncomingMessage);
    instance.on("message", handleIncomingMessage);

    instance.on("chat:unread:update", (payload: any) => {
      console.log(now(), "[chatSocket] chat:unread:update", payload);
      try {
        const id = String(
          payload?.conversationId ?? payload?.id ?? payload?.room ?? ""
        );
        if (!id) return;
        const count = Number(payload?.unread ?? payload?.count ?? 0);
        setUnreadMap((prev) => ({ ...prev, [id]: count }));
      } catch (e) {
        console.warn(now(), "chat:unread:update parse failed", e);
      }
    });

    const handleLeaveCreated = (payload: any) => {
      try {
        console.debug(now(), "[chatSocket] leave:created", payload);
        if (typeof payload?.unreadCount === "number") {
          setLeaveCount(payload.unreadCount);
        } else {
          setLeaveCount((prev) => prev + 1);
        }
      } catch (e) {
        console.warn(now(), "[chatSocket] leave:created handler failed", e);
      }
    };
    instance.on("leave:created", handleLeaveCreated);

    instance.on("connect_error", (err: any) =>
      console.warn(now(), " connect_error", err)
    );
    instance.on("disconnect", (reason: any) => {
      console.debug(now(), " disconnected", reason);
    });

    const beforeUnloadHandler = () => {
      try {
        if (localUserId && instance && instance.connected) {
          instance.emit("identify", { userId: localUserId, online: false });
        }
      } catch {}
    };
    window.addEventListener("beforeunload", beforeUnloadHandler);

    return () => {
      try {
        instance.off("chat:message", handleIncomingMessage);
        instance.off("chat:new_message", handleIncomingMessage);
        instance.off("message", handleIncomingMessage);
        instance.off("user:online");
        instance.off("user:offline");
        // NEW: remove leave listener
        try {
          instance.off("leave:created", handleLeaveCreated);
        } catch {}
        window.removeEventListener("beforeunload", beforeUnloadHandler);

        try {
          if (localUserId && instance && instance.connected) {
            instance.emit("identify", { userId: localUserId, online: false });
          }
        } catch {}

        instance.disconnect();
      } catch {}
      // clear pending offline timers
      Object.values(pendingOfflineTimers).forEach((t) =>
        window.clearTimeout(t)
      );
      setSocket(null);
      onlineRef.current.clear();
      setOnlineUsers([]);
      Object.values(typingTimers.current).forEach((t) =>
        window.clearTimeout(t)
      );
      typingTimers.current = {};
      joinedRooms.current.clear();
      setUnreadMap({});
      // reset leaveCount on teardown (keeps behavior predictable)
      setLeaveCount(0);
    };
  }, [userId, token, syncOnline, localUserId]);

  const isUserOnline = useCallback(
    (id?: string | null) => {
      if (!id) return false;
      console.log(now(), "isUserOnline check for", id, onlineRef.current);
      return onlineRef.current.has(String(id));
    },
    [socket]
  );

  const joinConversation = useCallback(
    (conversationId?: string) => {
      if (!socket || !conversationId) return;
      if (joinedRooms.current.has(conversationId)) return;
      socket.emit("join", { room: conversationId }, (ack: any) => {
        console.debug("[chatSocket] join ack", conversationId, ack);
        joinedRooms.current.add(conversationId);
      });
    },
    [socket]
  );

  const leaveConversation = useCallback(
    (conversationId?: string) => {
      if (!socket || !conversationId) return;
      if (!joinedRooms.current.has(conversationId)) return;
      socket.emit("leave", { room: conversationId }, (ack: any) => {
        console.debug("[chatSocket] leave ack", conversationId, ack);
        joinedRooms.current.delete(conversationId);
      });
    },
    [socket]
  );

  const emitTyping = useCallback(
    (opts: { recipientId?: string; conversationId?: string }) => {
      if (!socket) return;
      const recipientId =
        opts.recipientId ?? (opts.conversationId as any) ?? null;
      const nowTs = Date.now();
      if (nowTs - lastTypingEmit.current < 200) {
      } else {
        lastTypingEmit.current = nowTs;
      }
      try {
        console.debug("[chatSocket] emit typing ->", {
          recipientId,
          isTyping: true,
        });
        socket.emit("chat:typing", { recipientId, isTyping: true });
      } catch (e) {
        console.warn("[chatSocket] emit typing failed", e);
      }
      const key = String(recipientId ?? "global");
      if (typingTimers.current[key])
        window.clearTimeout(typingTimers.current[key]);
      typingTimers.current[key] = window.setTimeout(() => {
        try {
          console.debug("[chatSocket] emit typing ->", {
            recipientId,
            isTyping: false,
          });
          socket.emit("chat:typing", { recipientId, isTyping: false });
        } catch (e) {
          console.warn("[chatSocket] emit typing stop failed", e);
        }
        delete typingTimers.current[key];
      }, 1500) as any;
    },
    [socket]
  );

  const emitRead = useCallback(
    (payload: { conversationId?: string }) => {
      if (!socket) return;
      const conversationId = payload.conversationId;
      try {
        socket.emit("chat:read", { conversationId });
      } catch {}
    },
    [socket]
  );

  const markConversationReadLocal = useCallback(
    (conversationId?: string) => {
      if (!conversationId) return;
      setUnreadMap((prev) => {
        if (!prev || !prev[String(conversationId)])
          return { ...prev, [String(conversationId)]: 0 };
        return { ...prev, [String(conversationId)]: 0 };
      });

      try {
        if (socket) {
          socket.emit("chat:read", { conversationId });
        }
      } catch (e) {
        // ignore
      }
    },
    [socket]
  );

  return {
    socket,
    onlineUsers,
    isUserOnline,
    joinConversation,
    leaveConversation,
    emitTyping,
    emitRead,
    unreadMap,
    markConversationReadLocal,
    lastMessage,
    // NEW: expose leaveCount so callers (e.g., Navbar) can show it
    leaveCount,
  };
}
